import { Box, Collapse, Indicator, Text, ThemeIcon, UnstyledButton } from '@mantine/core'
import type { IconProps } from '@tabler/icons-react'
import { IconAlertTriangleFilled, IconChevronRight } from '@tabler/icons-react'
import { observer } from 'mobx-react-lite'
import type { ComponentType } from 'react'
import { useMemo, useState } from 'react'
import { Link, matchRoute, useLocation, useRouter } from 'wouter'

import { theme } from '@/configs/theme'
import { useGlobalState } from '@/hooks/useGlobalState'
import * as classes from '@/styles/NavbarLinksGroup.css'
import type { CommonAction } from '@/utils/componentCommons'
import { getCommonActionPath, getCommonActionRoute } from '@/utils/componentCommons'
import { getRouteByName } from '@/utils/navigation'

export type LinksGroup = Omit<CommonAction, 'onClick'> & {
  icon?: ComponentType<IconProps>
  opened?: boolean
  links?: LinksGroup[]
  disabled?: boolean
  warning?: boolean
  href?: string
}

const NO_ADDITIONAL_ROUTES: NonNullable<LinksGroup['additionalCurrentRoutes']> = []

type NavbarLinksGroupProps = LinksGroup
type NavbarLinkProps = Omit<LinksGroup, 'links' | 'open'> & {
  chevron?: boolean
  nested?: boolean
  onClick?: () => void
  warning?: boolean
  href?: string
}

export function NavbarLinksGroup(props: NavbarLinksGroupProps) {
  const {
    to,
    label,
    links,
    icon: Icon,
    additionalCurrentRoutes = NO_ADDITIONAL_ROUTES,
    disabled,
    warning,
    href,
  } = props

  const [opened, setOpened] = useState(props.opened ?? false)

  const hasNestedLinks = !!links?.length

  function toggle() {
    if (disabled) return
    if (to) return
    if (!hasNestedLinks) return
    setOpened(o => !o)
  }

  return (
    <>
      <NavbarLink
        label={label}
        icon={Icon}
        opened={opened}
        chevron={hasNestedLinks}
        onClick={toggle}
        to={to}
        additionalCurrentRoutes={additionalCurrentRoutes}
        disabled={disabled}
        warning={warning}
        href={href}
      />

      {hasNestedLinks && (
        <Collapse expanded={opened} className={theme.cx(classes.collapse)}>
          {links.map((l, index) => (
            <NavbarLink
              nested
              key={typeof l.label === 'string' ? l.label : index}
              label={l.label}
              icon={l.icon}
              to={l.to}
              additionalCurrentRoutes={l.additionalCurrentRoutes}
              disabled={l.disabled}
            />
          ))}
        </Collapse>
      )}
    </>
  )
}

const NavbarLink = observer(function NavbarLink(props: NavbarLinkProps) {
  const {
    label,
    icon: Icon,
    chevron = false,
    opened = false,
    nested = false,
    onClick,
    to,
    additionalCurrentRoutes = NO_ADDITIONAL_ROUTES,
    disabled = false,
    warning = false,
    href,
  } = props
  const { authenticationStore } = useGlobalState()
  const routerParser = useRouter().parser
  const currentLocation = useLocation()[0]

  const path = useMemo(() => getCommonActionPath(to), [to])
  const route = useMemo(() => getCommonActionRoute(to), [to])

  const active = useMemo(() => {
    if (disabled) return false
    if (!nested && opened) return false

    const toPattern = typeof to === 'object' && 'name' in to ? getRouteByName(to.name)?.path : '/404'
    const additionalPatterns = additionalCurrentRoutes.map(route => getRouteByName(route)?.path)
    const patterns = [toPattern, ...additionalPatterns].filter(Boolean) as string[]

    return patterns.some(pattern => matchRoute(routerParser, pattern, currentLocation)[0])
  }, [currentLocation, nested, opened, disabled, to, additionalCurrentRoutes, routerParser])

  const shouldRender = route?.renderConditionFn?.({ auth: authenticationStore }) ?? true

  if (shouldRender !== true) return null

  const handleClick = disabled ? undefined : onClick

  const linkComponent = href ? 'a' : disabled || !path ? undefined : Link
  const linkProps = href ? { href, target: '_blank', rel: 'noopener noreferrer' } : { to: disabled ? undefined : path }

  return (
    <UnstyledButton
      className={theme.cx([
        classes.linkContainer,
        active && classes.linkContainerActive,
        disabled && classes.linkContainerDisabled,
      ])}
      component={linkComponent as any}
      onClick={handleClick}
      {...linkProps}>
      <Box className={theme.cx([classes.linkWrapper, nested && classes.linkWrapperNested])}>
        {!!Icon && (
          <Indicator
            disabled={!warning}
            color="orange"
            size={16}
            offset={4}
            label={<IconAlertTriangleFilled size={10} />}
            styles={{ indicator: { padding: 2 } }}>
            <ThemeIcon
              variant={active && !disabled ? 'filled' : 'light'}
              size={nested ? 32 : 40}
              className={disabled ? classes.linkIconDisabled : undefined}>
              <Icon className={theme.cx([classes.linkIcon, nested && classes.linkIconNested])} />
            </ThemeIcon>
          </Indicator>
        )}

        <Text
          ml="md"
          size="sm"
          c={nested ? 'dimmed' : disabled ? 'dimmed' : undefined}
          fw={500}
          className={theme.cx([classes.linkLabel, disabled && classes.linkLabelDisabled])}>
          {label}
        </Text>
      </Box>

      {chevron && (
        <IconChevronRight
          className={theme.cx([classes.caret, opened && classes.caretRotated, disabled && classes.linkIconDisabled])}
          stroke={1.5}
        />
      )}
    </UnstyledButton>
  )
})
